import { Controller } from "@hotwired/stimulus";
import { registerMermaidContainer, renderMermaidBlocks, unregisterMermaidContainer } from "../lib/mermaid-renderer.js";

export default class extends Controller {
  static targets = ["contents", "tocBlank", "tocContainer"];
  static values = {
    scrollOffset: {
      type: Object,
      default: { desktop: 200, mobile: 100 },
    },
    autoScroll: {
      type: Boolean,
      default: true,
    },
  };

  connect() {
    this.isShown = true; // Default value
    this.toc = [];
    this.idCounts = {}; // to track ID usage for uniqueness
    this.handleCopy = this.handleCopy.bind(this);
    document.addEventListener("copy", this.handleCopy);
    this.getContents();
    registerMermaidContainer(this.contentsTarget);
    renderMermaidBlocks(this.contentsTarget).catch((error) => {
      console.error('Mermaid rendering failed:', error);
    });
    if (this.autoScrollValue) {
      this.attemptScroll(); // to scroll to the correct section on page load
    }
  }

  disconnect() {
    document.removeEventListener("copy", this.handleCopy);

    if (this.hasContentsTarget) {
      unregisterMermaidContainer(this.contentsTarget);
    }
  }

  handleCopy(event) {
    if (!event.clipboardData) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    if (!this.contentsTarget.contains(range.commonAncestorContainer)) return;

    const container = document.createElement("div");
    container.appendChild(range.cloneContents());

    event.clipboardData.setData("text/html", container.innerHTML);
    event.clipboardData.setData("text/plain", selection.toString());
    event.preventDefault();
  }

  get scrollOffset() {
    const vw = window.innerWidth;
    const { desktop, mobile } = this.scrollOffsetValue;

    return vw > 1200 ? desktop : mobile;
  }

  getContents() {
    const headers = this.contentsTarget.querySelectorAll(
      "h1, h2, h3, h4, h5, h6"
    );
    headers.forEach((element) => {
      const readable = element.textContent.trim();
      if (!readable) return;

      const tag = element.tagName;
      // Changed: Use generateUniqueId instead of simple replacement + random number
      // This ensures consistent IDs across page loads and handles duplicates
      // We removed the Math.random() part because:
      // 1. It made IDs inconsistent across page loads
      // 3. It made it impossible to reliably link to specific sections
      let id = this.generateUniqueId(readable);
      element.id = id;
      this.toc.push({ tag: tag, name: readable, id: id });
    });
    if (this.hasTocTargets()) {
      this.renderToc();
    }
  }

  // New method to generate unique IDs
  generateUniqueId(text) {
    // Generate a URL-friendly base ID
    let baseId = text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");

    // Make the ID is unique even if the header text is the same
    if (this.idCounts[baseId]) {
      this.idCounts[baseId]++;
      return `${baseId}-${this.idCounts[baseId]}`;
    } else {
      this.idCounts[baseId] = 1;
      return baseId;
    }
  }

  renderToc() {
    if (!this.hasTocTargets()) return;

    if (this.toc.length == 0) {
      this.tocBlankTarget.style.display = "block";
      this.element.classList.add("no-toc");
    } else {
      this.tocBlankTarget.style.display = "none";
      this.element.classList.remove("no-toc");
      const ul = document.createElement("ul");

      this.toc.forEach((t) => {
        const li = document.createElement("li");
        li.className = `toc${t.tag}`;
        const a = document.createElement("a");
        a.href = `#${t.id}`;
        a.textContent = t.name;
        a.dataset.targetId = t.id;

        a.dataset.action = "click->kb-content#handleTocClick";

        li.appendChild(a);
        ul.appendChild(li);
      });

      this.tocContainerTarget.appendChild(ul);
    }
  }

  hasTocTargets() {
    return this.hasTocBlankTarget && this.hasTocContainerTarget;
  }

  handleTocClick(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const targetId = event.currentTarget.dataset.targetId;
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    const scrollOffset = this.scrollOffset;
    const elementTop =
      targetElement.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementTop - scrollOffset,
      behavior: "smooth",
      left: 0,
    });

    history.pushState(null, "", `#${targetId}`);
  }

  attemptScroll() {
    if (window.location.hash) {
      const targetId = window.location.hash.slice(1);
      setTimeout(() => {
        this.scrollToElement(targetId);
      }, 100);
    } else {
      this.scrollToTop();
    }
  }

  scrollToElement(targetId, attempts = 0) {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const scrollOffset = this.scrollOffset;
      const elementTop =
        targetElement.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: elementTop - scrollOffset,
        behavior: "smooth",
      });
    } else if (attempts < 5) {
      setTimeout(() => this.scrollToElement(targetId, attempts + 1), 200);
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }
}
